import React, { useEffect, useState } from 'react'
import { AppProps } from 'next/app'
import Head from 'next/head'
import { QueryClientProvider, HydrationBoundary } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { pianoAnalytics } from 'piano-analytics-js'
import * as CookieConsent from 'vanilla-cookieconsent'
import 'vanilla-cookieconsent/dist/cookieconsent.css'

import { GlobalStyles, Theme } from 'src/theme'
import PianoScrollAnalytic from 'src/components/analytics/PianoScrollAnalytic'
import ErrorBoundary from 'src/components/pages/error/ErrorBoundary'
import { getQueryClient } from 'src/lib/react-query/client'

const AdSalesApp = ({ Component, pageProps }: AppProps) => {
  // Create a stable query client instance
  const [queryClient] = useState(() => getQueryClient())
  useEffect(() => {
    /**
     * All config. options available here:
     * https://cookieconsent.orestbida.com/reference/configuration-reference.html
     */
    CookieConsent.run({
      categories: {
        identifier: {
          enabled: false
        },
        adverts: { enabled: false }
      },
      language: {
        default: 'en',
        translations: {
          en: {
            consentModal: {
              title: 'We use cookies',
              description:
                "We and our 3 partners use cookies and similar technologies to give you the best experience and for statistical purposes to enable us to measure and improve our service. Our service providers, may combine data they collect with other information that you've provided to them or that they've collected from your use of their services.",
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject all',
              showPreferencesBtn: 'Manage Individual preferences'
            },
            preferencesModal: {
              title: 'Manage cookie preferences',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject all',
              savePreferencesBtn: 'Accept current selection',
              closeIconLabel: 'Close modal',
              sections: [
                {
                  description:
                    'Please accept the use of our cookies. We and select third parties use cookies and similar technologies to deliver and improve our online Services, understand how they are used, provide social media features, and to display relevant content, products, services and advertising for you. Our use of Cookies may involve the processing of Personal Data, such as IP address or other online mobile identifiers. To find out more, see our <a href="https://www.bbcstudios.com/cookies">Cookie Notice</a>. To customize your preferences please click “Configure Preferences.”'
                },
                {
                  title: 'Store and/or access information on a device',
                  description:
                    'Cookies, device or similar online identifiers (e.g. login-based identifiers, randomly assigned identifiers, network based identifiers) together with other information (e.g. browser type and information, language, screen size, supported technologies etc.) can be stored or read on your device to recognise it each time it connects to an app or to a website, for one or several of the purposes presented here.',
                  linkedCategory: 'identifier'
                },
                {
                  title:
                    'Personalised advertising and content, advertising and content measurement, audience research and services development',
                  description:
                    'Advertising and content can be personalised based on your profile. Your activity on this service can be used to build or improve a profile about you for personalised advertising and content. Advertising and content performance can be measured. Reports can be generated based on your activity and those of others. Your activity on this service can help develop and improve products and services.',
                  linkedCategory: 'adverts'
                }
              ]
            }
          }
        }
      }
    })
  }, [])

  return (
    <>
      <Head>
        {(() => {
          const content = pianoAnalytics.setConfigurations({
            collectDomain: 'a1.api.bbc.com',
            path: 'event',
            site: 647063,
            addEventURL: 'true',
            enableUTMTracking: true,
            campaignPrefix: ['at_', 'utm_']
          })
          return Array.isArray(content)
            ? content.map((child, i) =>
                React.isValidElement(child)
                  ? React.cloneElement(child as React.ReactElement<{ key?: string }>, {
                      key: (child as React.ReactElement).key ?? `piano-${i}`
                    })
                  : child
              )
            : content
        })()}
      </Head>
      <QueryClientProvider client={queryClient}>
        <HydrationBoundary state={pageProps.dehydratedState}>
          <Theme>
            <GlobalStyles />
            <ErrorBoundary>
              <PianoScrollAnalytic previousPageUrl={pageProps?.previousPage}>
                <Component {...pageProps} />
                <ReactQueryDevtools initialIsOpen={false} />
              </PianoScrollAnalytic>
            </ErrorBoundary>
          </Theme>
        </HydrationBoundary>
      </QueryClientProvider>
    </>
  )
}

export default AdSalesApp
